import {
  lineString
} from "@turf/turf";

import {GeoJSONType} from "../types/geoJSON";
import {getCoordinatesFromGeoJSON} from "./geoJSONHelper";
import {CoordinatesType, PositionType, QuadrilateralCornersType, QuadrilateralPerimeterSidesType} from "../types/shape";
import {
  getConvexHullCoordinatesFrom,
  isAQuadrilateralFromCoordinates, samePositions, sliceAwayLastDuplicatedCoordinates,
  sortCoordinatesLatASC,
  sortCoordinatesLongASC,
} from "./coordinatesHelper";
import {calculateBearingAngleBetween} from "./shapeHelper";

export const convertToQuadrilateralGeoJSONFrom = (
  geoJson: GeoJSONType
): GeoJSONType => {
  const polygonCoords = getCoordinatesFromGeoJSON(geoJson);
  return {
    ...geoJson,
    geometry: {
      type: "Polygon",
      coordinates: [convertToQuadrilateralCoordinatesFrom(polygonCoords)]
    }
  }
};

export const convertToQuadrilateralCoordinatesFrom = (
  polygonCoords: CoordinatesType
): CoordinatesType => {
  let coords = polygonCoords;

  if (!isAQuadrilateralFromCoordinates(coords)) {
    coords = getConvexHullCoordinatesFrom(coords)
    if (!isAQuadrilateralFromCoordinates(coords)) {
      coords = removeMiddlePointsFromCoordinates(coords)
    }
  }

  const {
    upLeft,
    upRight,
    bottomLeft,
    bottomRight
  } = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(coords);

  return [upLeft, upRight, bottomRight, bottomLeft, upLeft];
};

const removeMiddlePointsFromCoordinates = (
  polygonCoords: CoordinatesType
): CoordinatesType => {
  const uniqueCoords = filterUniqueCoordinatesFrom(polygonCoords);
  const deltaBearingAngles = uniqueCoords.map((current, index) => {
    const prev = index === 0
      ? uniqueCoords[uniqueCoords.length - 1]
      : uniqueCoords[index - 1];
    const next = uniqueCoords[(index + 1) % uniqueCoords.length];

    return calculateDeltaBearingAngle(prev, current, next)
  })
  // make a copy of deltaBearingAngles and reorder
  // sorted DESC order, take the forth greater delta Bearing Angle
  const minimumBearingAngle = deltaBearingAngles.slice(0).sort((a, b) => b - a)[3];

  const coords = uniqueCoords.filter((e, index) => deltaBearingAngles[index] >= minimumBearingAngle);
  coords.push(coords[0]);
  return coords;
};

const filterUniqueCoordinatesFrom = (polygonCoords: CoordinatesType): CoordinatesType => {
  const allCoords = sliceAwayLastDuplicatedCoordinates(polygonCoords);
  const uniqueCoords: CoordinatesType = []

  allCoords.forEach((position) => {
    if (!uniqueCoords.some((p) => samePositions(p, position))) {
      uniqueCoords.push(position)
    }
  })

  return uniqueCoords;
}

const calculateDeltaBearingAngle = (prev: PositionType, current: PositionType, next: PositionType): number => {
  const bearingAngle1 = calculateBearingAngleBetween(prev, current);
  const bearingAngle2 = calculateBearingAngleBetween(current, next);
  return (bearingAngle1 >= 0 && bearingAngle2 >= 0) || (bearingAngle1 < 0 && bearingAngle2 < 0)
    ? Math.abs(Math.abs(bearingAngle1) - Math.abs(bearingAngle2))
    : (360 + Math.min(bearingAngle1, bearingAngle2)) - Math.max(bearingAngle1, bearingAngle2)
}

export const getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates = (
  coords: CoordinatesType
): QuadrilateralPerimeterSidesType => {
  const {
    upLeft,
    upRight,
    bottomLeft,
    bottomRight
  } = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(coords);

  return {
    bottom: lineString([bottomLeft, bottomRight]),
    left: lineString([bottomLeft, upLeft]),
    top: lineString([upLeft, upRight]),
    right: lineString([bottomRight, upRight])
  }
};

export const getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates = (
  coords: CoordinatesType
): QuadrilateralCornersType => {
  if (!isAQuadrilateralFromCoordinates(coords)) {
    throw new Error(`It is not quadrilateral coordinates ${coords}`);
  }

  const [bottom1, bottom2, upper1, upper2] = sortCoordinatesLatASC(coords);
  const [bottomLeft, bottomRight] = sortCoordinatesLongASC([bottom1, bottom2]);
  const [upLeft, upRight] = sortCoordinatesLongASC([upper1, upper2]);

  return {
    upLeft,
    upRight,
    bottomLeft,
    bottomRight
  }
};

export const getQuadrilateralPerimeterCornersFromAnyCoordinates = (
  coords: CoordinatesType
): QuadrilateralCornersType => {
  return getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
    convertToQuadrilateralCoordinatesFrom(coords)
  )
};
